import { ComponentProps, forwardRef } from "react";
import { Img, ImgProps } from "../base/img";
import { InUseIcon, PremiumIcon } from "./AvatarIcons";
import { useTranslation } from "@vixi/localization";
import { downloadFile } from "@vixi/api";

// Assets
import DeleteIcon from "@assets/icons/delete.svg?react";
import { cn } from "@/lib/utils";
import { Button, ButtonProps } from "../base/button";
import { DownloadIcon } from "lucide-react";

// Styling
const AvatarGridItemIcon = ({ className, ...props }: ComponentProps<"div">) => (
  <div className={cn("absolute end-3.25 top-2.75 z-1", className)} {...props} />
);

const AvatarGridItemRoot = ({
  premium,
  selected,
  className,
  ...props
}: ComponentProps<"div"> & Pick<AvatarGridItemProps, "premium" | "selected">) => (
  <div
    className={cn(
      "bg-paper relative flex h-full flex-col overflow-hidden rounded-md hover:[&_.hover-overlay]:opacity-100",
      {
        "border-primary border-2": selected,
        "border-premium": premium,
        className,
      },
    )}
    {...props}
  />
);

const AvatarGridItemHoverOverlay = ({ className, ...props }: ComponentProps<"div">) => (
  <div
    className={cn(
      "hover-overlay",
      "absolute inset-0 mx-auto flex h-full w-full flex-col bg-black/25 opacity-0 transition-opacity duration-200 hover:opacity-100",
      className,
    )}
    {...props}
  />
);

const AvatarGridItemImage = ({ className, ...props }: ImgProps) => (
  <Img
    className={cn("aspect-896/1344 max-h-118.75 min-h-37.5 w-full flex-1 object-contain", className)}
    {...props}
  />
);

const AvatarGridItemUnlockButton = ({ className, ...props }: ButtonProps) => {
  return (
    <Button
      className={cn("mb-3.5px ar:font-semibold mx-auto mt-0 block border", className)}
      {...props}
      variant="outlined"
      size="pill"
      color="premium"
    />
  );
};

const AvatarGridItemOptions = ({ className, ...props }: ComponentProps<"div">) => {
  return (
    <div
      className={cn("flex items-center justify-between ps-7.25 pe-5.75 pt-0 pb-3.5", className)}
      {...props}
    />
  );
};

const AvatarGridItemName = ({ className, ...props }: ComponentProps<"p">) => (
  <p className={cn("text-foreground text-[13px] font-semibold", className)} {...props} />
);

// Props
export interface AvatarGridItemActions {
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onUseAvatar?: () => void;
}

interface AvatarGridItemProps extends ComponentProps<"div">, AvatarGridItemActions {
  label?: string;
  image: string;
  name?: string;
  hoverOverlay?: React.ReactNode;
  premium?: boolean;
  useAvatarText?: string;
  selected?: boolean;
  showDownload?: boolean;
  showIcon?: boolean;
}

export const AvatarGridItem = forwardRef<HTMLDivElement, AvatarGridItemProps>(
  (
    {
      label,
      name,
      image,
      premium,
      selected,
      hoverOverlay,
      onClick,
      onUseAvatar,
      useAvatarText,
      showIcon = true,
      showDownload = true,
      onDelete,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();

    return (
      <AvatarGridItemRoot {...props} selected={selected} premium={premium} ref={ref}>
        {label && <p>{label}</p>}

        <AvatarGridItemImage src={image} />

        {showIcon && (
          <AvatarGridItemIcon>
            {premium ? <PremiumIcon /> : <InUseIcon active={selected} />}
          </AvatarGridItemIcon>
        )}

        {hoverOverlay && <AvatarGridItemHoverOverlay>{hoverOverlay}</AvatarGridItemHoverOverlay>}

        {premium ? (
          <AvatarGridItemUnlockButton onClick={() => onUseAvatar && onUseAvatar()}>
            {useAvatarText ?? t("Avatars.UseThisAvatar")}
          </AvatarGridItemUnlockButton>
        ) : (
          <AvatarGridItemOptions>
            <AvatarGridItemName>{name}</AvatarGridItemName>

            <div className="flex gap-1">
              {showDownload && (
                <Button
                  onClick={() => downloadFile(image, image.split("/").pop() ?? "avatar.png")}
                  color="foreground"
                  variant="text"
                  size="icon"
                  className="hover:text-primary"
                >
                  <DownloadIcon fontSize={16} />
                </Button>
              )}

              {!!onDelete && (
                <Button
                  color="foreground"
                  variant="text"
                  size="icon"
                  className="hover:text-destructive"
                  onClick={onDelete}
                >
                  <DeleteIcon fontSize={16} />
                </Button>
              )}
            </div>
          </AvatarGridItemOptions>
        )}
      </AvatarGridItemRoot>
    );
  },
);

export default AvatarGridItem;
